/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef,
} from 'react';

import { jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ButtonProps } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import SelectAllIcon from '@atlaskit/icon/core/presenter-mode';
import { CustomItem, type CustomItemComponentProps } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import type { AnyEntryPoint } from '@atlassian/entry-point-types';

import type { ToolbarAction } from '../common/types';
import {
	FloatingToolbarContainer,
	FloatingToolbarContent,
	FloatingToolbarSeparator,
	FloatingToolbarSideContent,
} from '../common/ui';

import messages from './messages';
import ShowMoreButton from './show-more-button';
import { ToolbarButton } from './toolbar-button';
import type { ToolbarButtonProps } from './toolbar-button/types';
import { calculateOverflowButtons, initialOverflowState, overflowReducer } from './utils';

const BULK_OPS_SELECTED_ISSUE_LIMIT = 1000;

const styles = cssMap({
	selectedContainer: {
		font: token('font.body'),
		color: token('color.text'),
		height: '16px',
	},
	selectedContainerNew: {
		flexWrap: 'nowrap',
	},
	numberContainer: {
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		height: '16px',
		minWidth: '24px',
		marginRight: token('space.050'),
		borderRadius: token('radius.xsmall'),
		display: 'flex',
		justifyContent: 'center',
		alignContent: 'center',
		flexWrap: 'wrap',
		alignItems: 'center',
	},
});

// both CustomItemComponentProps and ToolbarButtonProps have the prop isDisabled
// the former ends up swallowing it and not passing it down to the latter
// hence there is a separate prop isToolbarButtonDisabled to be able to pass it down
type CustomComponentWithToolbarProps = CustomItemComponentProps &
	ToolbarButtonProps & { isToolbarButtonDisabled?: boolean };

const ToolbarButtonComponent = ({
	action,
	isToolbarButtonDisabled,
	tabIndex,
	buttonRef,
}: CustomComponentWithToolbarProps) => {
	return (
		<ToolbarButton
			tabIndex={tabIndex}
			action={action}
			isDisabled={isToolbarButtonDisabled}
			buttonRef={buttonRef}
			isInMeatballMenu
		/>
	);
};

export type FloatingToolbarProps<TEntryPoint extends AnyEntryPoint | undefined = undefined> = {
	actions?: ToolbarAction<TEntryPoint>[];
	isAllSelected: boolean;
	maxToolbarWidth?: number;
	numberOfSelectedItems: number;
	onClearAll: ButtonProps['onClick'];
	onSelectAll?: ButtonProps['onClick'];
	shouldDisableButtons?: boolean;
	showSelectFirstThreshold?: boolean;
};

export const FloatingToolbar = <TEntryPoint extends AnyEntryPoint | undefined = undefined>({
	numberOfSelectedItems,
	actions,
	shouldDisableButtons,
	onClearAll,
	onSelectAll,
	showSelectFirstThreshold,
	maxToolbarWidth,
}: FloatingToolbarProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const buttonsRefs = useRef(new Map<number, HTMLButtonElement | null>());
	const clearAllButtonRef = useRef<HTMLButtonElement | null>(null);
	const selectAllButtonRef = useRef<HTMLButtonElement | null>(null);
	const moreButtonRef = useRef<HTMLButtonElement | null>(null);
	const toolbarContainerRef = useRef<HTMLElement | null>(null);
	const buttonContainerRef = useRef<HTMLElement | null>(null);
	const selectedTextRef = useRef<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ mustRecalculateOverflowButtons, overflowButtons }, dispatch] = useReducer(
		overflowReducer,
		initialOverflowState,
	);

	useEffect(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'bulkToolbar viewed', 'bulkFloatingToolbar', {});
	}, [createAnalyticsEvent]);

	const buttons = useMemo(() => {
		const buttonsComponents = actions?.map((action, index) => {
			return (
				<ToolbarButton
					tabIndex={!onSelectAll && index === 0 ? 0 : -1}
					key={index}
					action={action}
					isDisabled={shouldDisableButtons}
					buttonRef={(elem: HTMLButtonElement) => {
						const mapElem = elem || null;
						buttonsRefs.current.set(index, mapElem);
					}}
				/>
			);
		});

		return buttonsComponents;
	}, [onSelectAll, actions, shouldDisableButtons]);

	const visibleButtons = useMemo(() => {
		return actions
			?.filter((_, index) => !overflowButtons.has(index))
			.map((action, index) => (
				<ToolbarButton
					tabIndex={!onSelectAll && index === 0 ? 0 : -1}
					key={index}
					action={action}
					isDisabled={shouldDisableButtons}
					buttonRef={(elem: HTMLButtonElement) => {
						const mapElem = elem || null;
						buttonsRefs.current.set(index, mapElem);
					}}
				/>
			));
	}, [actions, onSelectAll, overflowButtons, shouldDisableButtons]);

	const hiddenButtons = useMemo(() => {
		return actions
			?.filter((_, index) => overflowButtons.has(index))
			.map((action, index) => (
				<CustomItem
					key={index}
					component={ToolbarButtonComponent}
					action={action}
					isToolbarButtonDisabled={shouldDisableButtons}
					buttonRef={(elem: HTMLButtonElement) => {
						const mapElem = elem || null;
						buttonsRefs.current.set(
							visibleButtons ? visibleButtons.length + index : index,
							mapElem,
						);
					}}
				/>
			));
	}, [actions, overflowButtons, shouldDisableButtons, visibleButtons]);

	const onKeyDownHandler = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
				e.stopPropagation();

				if (e.key === 'End') {
					clearAllButtonRef.current?.focus();
					return;
				}
				if (e.key === 'Home') {
					if (onSelectAll) {
						selectAllButtonRef.current?.focus();
					} else {
						buttonsRefs.current.get(0)?.focus();
					}
					return;
				}
				if (typeof document !== 'undefined') {
					const middleButtons = Array.from(buttonsRefs.current.values()).filter(
						(item): item is HTMLButtonElement => Boolean(item),
					);

					const allButtons = [
						...(selectAllButtonRef.current ? [selectAllButtonRef.current] : []),
						...middleButtons,
						...(moreButtonRef.current ? [moreButtonRef.current] : []),
						...(clearAllButtonRef.current ? [clearAllButtonRef.current] : []),
					];
					const focusedElement = document.activeElement;
					const moveLeft = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
					const moveRight = e.key === 'ArrowDown' || e.key === 'ArrowRight';
					const focusedIndex = allButtons.findIndex((b) => b === focusedElement);

					let newFocus: number | undefined;
					if (moveRight) {
						if (focusedIndex === allButtons.length - 1) {
							newFocus = 0;
						} else {
							newFocus = focusedIndex + 1;
						}
					}
					if (moveLeft) {
						if (focusedIndex === 0) {
							newFocus = allButtons.length - 1;
						} else {
							newFocus = focusedIndex - 1;
						}
					}
					if (newFocus !== undefined) {
						allButtons[newFocus].focus();
					}
				}
			}
		},
		[onSelectAll],
	);

	const debouncedCalculateOverflow = useMemo(
		() =>
			debounce(
				() => {
					dispatch({ type: 'SET_MUST_RECALCULATE' });
				},
				300,
				{ leading: true },
			),
		[],
	);

	useLayoutEffect(() => {
		if (fg('responsive_bulk_ops_toolbar_platform')) {
			debouncedCalculateOverflow();
		}
		// we want the calculation to run again if maxToolbarWidth changes
	}, [debouncedCalculateOverflow, maxToolbarWidth]);

	const usedToolBarWidth =
		(selectedTextRef.current?.offsetWidth || 0) +
		(selectAllButtonRef.current?.offsetWidth || 0) +
		(moreButtonRef.current?.offsetWidth || 0) +
		(clearAllButtonRef.current?.offsetWidth || 0) +
		17 * 2; // separators

	useLayoutEffect(() => {
		if (mustRecalculateOverflowButtons && fg('responsive_bulk_ops_toolbar_platform')) {
			const newOverflowButtons = calculateOverflowButtons(
				toolbarContainerRef,
				buttonsRefs.current,
				buttonContainerRef,
				usedToolBarWidth,
				actions || [],
			);
			dispatch({ type: 'SET_OVERFLOW_BUTTONS', payload: newOverflowButtons });
		}
	}, [actions, mustRecalculateOverflowButtons, usedToolBarWidth]);

	useLayoutEffect(() => {
		if (fg('responsive_bulk_ops_toolbar_platform')) {
			const resizeObserver = new ResizeObserver(debouncedCalculateOverflow);

			if (toolbarContainerRef.current) {
				resizeObserver.observe(toolbarContainerRef.current);
			}

			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [debouncedCalculateOverflow]);

	// The formatted message below was previous wrapped in <h2 aria-live="polite" css={styles.selectedContainer}>
	// for accessibility reasons. However due to racheting we are unable to reintroduce this.
	// https://hello.jira.atlassian.cloud/browse/DEE-8591

	return (
		<FloatingToolbarContainer
			onKeyDown={onKeyDownHandler}
			innerRef={fg('responsive_bulk_ops_toolbar_platform') ? toolbarContainerRef : undefined}
			maxWidth={fg('responsive_bulk_ops_toolbar_platform') ? maxToolbarWidth : undefined}
		>
			<FloatingToolbarContent
				innerRef={fg('responsive_bulk_ops_toolbar_platform') ? buttonContainerRef : undefined}
			>
				<FloatingToolbarSideContent
					innerRef={fg('responsive_bulk_ops_toolbar_platform') ? selectedTextRef : undefined}
				>
					<Inline
						xcss={cx(
							styles.selectedContainer,
							fg('responsive_bulk_ops_toolbar_platform') && styles.selectedContainerNew,
						)}
						alignBlock="center"
						alignInline="center"
						shouldWrap={false}
					>
						<FormattedMessage
							{...messages.selectedItemsLabel}
							values={{
								numberOfSelectedItems,
								numberSelected: (
									<Box
										paddingInlineStart="space.050"
										paddingInlineEnd="space.050"
										xcss={styles.numberContainer}
									>
										{numberOfSelectedItems}
									</Box>
								),
							}}
						/>
					</Inline>
				</FloatingToolbarSideContent>
				{onSelectAll && (
					<ToolbarButton
						tabIndex={0}
						isDisabled={shouldDisableButtons}
						buttonRef={selectAllButtonRef}
						action={{
							icon: SelectAllIcon,
							name: showSelectFirstThreshold
								? formatMessage(messages.selectFirstButtonLabel, {
										numberOfIssues: BULK_OPS_SELECTED_ISSUE_LIMIT,
									})
								: formatMessage(messages.selectAllButtonLabel),
							type: 'perform',
							onClick: onSelectAll,
						}}
					/>
				)}
				<FloatingToolbarSeparator />
				{fg('responsive_bulk_ops_toolbar_platform') ? (
					<>
						{visibleButtons}
						<ShowMoreButton hiddenButtons={hiddenButtons} buttonRef={moreButtonRef} />
						<FloatingToolbarSeparator />
					</>
				) : (
					buttons
				)}
				<ToolbarButton
					tabIndex={-1}
					isDisabled={shouldDisableButtons}
					buttonRef={clearAllButtonRef}
					action={{
						icon: CrossIcon,
						name: formatMessage(messages.clearAllButton),
						type: 'perform',
						onClick: onClearAll,
						iconOnly: true,
					}}
				/>
			</FloatingToolbarContent>
		</FloatingToolbarContainer>
	);
};
